import { dateIsFullyPopulated } from '../components/form/DateInputField/utils';
import type { DisabledRule, FormConfig, Page, Question, Section, Subsection } from '../config/types';
import type { FetchFormResponse } from '../service/types';
import { Status } from '../types';
import { isObject } from './array-utils';
import { getListAnswers, hasValue } from './form-utils';
import { getLondonDateParts } from './londonTime';
import {
  getDisplayLabelForStoredSelectValue,
  getSelectOptionValue,
} from './select-assets';

const COMPLETED_SUBSECTION_STATUSES = new Set<Status>([
  Status.Completed,
  Status.NilReturn,
  Status.NotDue,
]);
type QuarterCode = 'Q1' | 'Q2' | 'Q3' | 'Q4';

const QUARTER_SORT_ORDER: Record<QuarterCode, number> = {
  Q1: 1,
  Q2: 2,
  Q3: 3,
  Q4: 4,
};

export const normalizeQuarterCode = (value: unknown): QuarterCode | '' => {
  const match = String(value || '')
    .trim()
    .toUpperCase()
    .match(/\bQ([1-4])\b/);
  return match?.[1] ? (`Q${match[1]}` as QuarterCode) : '';
};

export const canEditApprovalBeforeClose = ({
  approvalCloseOnQuarter,
  formState,
  role,
  pendingApprovalQuarter,
}: {
  approvalCloseOnQuarter?: FormConfig['approvalCloseOnQuarter'];
  formState?: unknown;
  role?: unknown;
  pendingApprovalQuarter?: unknown;
}): boolean => {
  const closeQuarter = normalizeQuarterCode(approvalCloseOnQuarter);
  if (!closeQuarter) return false;

  if (String(formState || '').trim().toLowerCase() !== 'approval') return false;

  const normalizedRole = String(role || '').trim().toLowerCase();
  if (normalizedRole !== 'owner' && normalizedRole !== 'editor') return false;

  const pendingQuarter = normalizeQuarterCode(pendingApprovalQuarter);
  if (!pendingQuarter) return false;

  return QUARTER_SORT_ORDER[pendingQuarter] <= QUARTER_SORT_ORDER[closeQuarter];
};

const getSectionQuarter = (section: Section): QuarterCode | '' => {
  return section.quarterReview?.quarter || '';
};

export const isApprovedQuarterSection = (
  section: Section,
  data?: FetchFormResponse,
): boolean => {
  const quarter = getSectionQuarter(section);
  return Boolean(
    quarter && !data?.staffCompletionRequiredQuarters?.includes(quarter) && (
      data?.approvedQuarters?.includes(quarter) ||
      normalizeQuarterCode(data?.approvedQuarter || data?.approvalStatus) === quarter
    ),
  );
};

export const getIncompleteRequiredSubsections = (
  sections: Section[],
  subsectionStatuses: Record<string, Status>,
  requireAllQuarters = false,
) =>
  sections.flatMap((section) => {
    const statuses = section.subsections.map(
      (subsection) => subsectionStatuses[subsection.slug] ?? Status.NotStarted,
    );
    if (section.quarterReview) {
      const started = statuses.some(
        (status) => status !== Status.NotStarted && status !== Status.NotDue,
      );
      if (!requireAllQuarters && section.quarterReview.quarter !== 'Q4' && !started) return [];
    }
    return section.subsections.flatMap((subsection, index) => {
      const status = statuses[index];
      const incomplete = section.quarterReview
        ? status !== Status.Completed && status !== Status.NilReturn
        : !COMPLETED_SUBSECTION_STATUSES.has(status);
      return incomplete ? [{ section, subsection }] : [];
    });
  });

export const hasIncompleteRequiredSubsections = (
  sections: Section[],
  subsectionStatuses: Record<string, Status>,
): boolean => getIncompleteRequiredSubsections(sections, subsectionStatuses).length > 0;

export const isApprovalPageName = (pageName: string): boolean =>
  pageName === 'approval' || pageName.startsWith('approval-');

export const isWorkflowOnlyPageName = (pageName: string): boolean =>
  isApprovalPageName(pageName) || pageName === 'review';

const parseReportPeriodQuarter = (value: unknown): string => {
  if (!value) return '';

  if (typeof value === 'object') {
    const parsed = value as { quarter?: unknown };
    return typeof parsed.quarter === 'string' ? parsed.quarter.trim() : '';
  }

  if (typeof value !== 'string') return '';

  try {
    return parseReportPeriodQuarter(JSON.parse(value));
  } catch (_err) {
    const match = value.match(/\b(?:quarter|q)\s*([1-4])\b/i);
    return match?.[1] ? `Q${match[1]}` : '';
  }
};

const getSelectedOptionValues = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.map(String);
  if (typeof value !== 'string' || !value.trim()) return [];

  try {
    return getSelectedOptionValues(JSON.parse(value));
  } catch (_err) {
    return [value.trim()];
  }
};

const matchesDisabledRule = (
  rule: DisabledRule,
  values: Record<string, unknown>,
): boolean => {
  const rawValue = values?.[rule.field];
  const normalizedRuleValue = String(rule.value || '').trim();
  const normalizedFieldValue = String(values?.[normalizedRuleValue] ?? '').trim();

  switch (rule.condition) {
    case 'notEquals':
      return String(rawValue ?? '').trim() !== normalizedRuleValue;
    case 'equalsField':
      return String(rawValue ?? '').trim() === normalizedFieldValue;
    case 'notEqualsField':
      return String(rawValue ?? '').trim() !== normalizedFieldValue;
    case 'numberGreaterThan': {
      const fieldNumber = Number(rawValue);
      const ruleNumber = Number(normalizedRuleValue);
      return Number.isFinite(fieldNumber) && Number.isFinite(ruleNumber) && fieldNumber > ruleNumber;
    }
    case 'reportPeriodQuarterEquals':
      return parseReportPeriodQuarter(rawValue).toUpperCase() === normalizedRuleValue.toUpperCase();
    case 'reportPeriodQuarterNotEquals':
      return parseReportPeriodQuarter(rawValue).toUpperCase() !== normalizedRuleValue.toUpperCase();
    case 'selectedOptionsNoneEqual': {
      const selectedOptions = getSelectedOptionValues(rawValue);
      if (selectedOptions.length === 0) return false;
      return selectedOptions.every((option) => {
        const answerField = rule.answerFieldsByOption?.[option];
        return !answerField || String(values?.[answerField] ?? '').trim() !== normalizedRuleValue;
      });
    }
    case 'equals':
    default:
      return String(rawValue ?? '').trim() === normalizedRuleValue;
  }
};

export const isQuestionDisabled = (
  question: Question,
  answers: FetchFormResponse['answers'],
): boolean =>
  Array.isArray(question.disabled) &&
  question.disabled.some((rule) => matchesDisabledRule(rule, answers));

export const isQuestionVisible = (
  question: Question,
  answers: FetchFormResponse['answers'],
): boolean =>
  !isQuestionDisabled(question, answers) &&
  (!Array.isArray(question.showWhen) ||
    question.showWhen.length === 0 ||
    question.showWhen.some((rule) => matchesDisabledRule(rule, answers)));

export const isPageDisabled = (
  page: Page,
  formValues: FetchFormResponse = { answers: {} },
): boolean => {
  if (!Array.isArray(page.disabled) || page.disabled.length === 0) return false;

  const combinedValues: Record<string, unknown> = {
    ...(formValues as unknown as Record<string, unknown>),
    ...(formValues.answers || {}),
  };

  return page.disabled.some((rule) => matchesDisabledRule(rule, combinedValues));
};

export const isSubsectionNotRequired = (
  subsection: Subsection,
  formValues: FetchFormResponse = { answers: {} },
): boolean => {
  if (!Array.isArray(subsection.notRequiredWhen) || subsection.notRequiredWhen.length === 0) {
    return false;
  }

  const combinedValues: Record<string, unknown> = {
    ...(formValues as unknown as Record<string, unknown>),
    ...(formValues.answers || {}),
  };

  return subsection.notRequiredWhen.some((rule) => matchesDisabledRule(rule, combinedValues));
};

const parseReportPeriodYear = (value: unknown): string => {
  if (!value) return '';

  if (typeof value === 'object') {
    const parsed = value as { year?: unknown };
    return typeof parsed.year === 'string' ? parsed.year.trim() : '';
  }

  if (typeof value !== 'string') return '';

  try {
    return parseReportPeriodYear(JSON.parse(value));
  } catch (_err) {
    const match = value.match(/\b(20\d{2}\s*[-/]\s*(?:\d{2}|\d{4}))\b/);
    return match?.[1]?.replace(/\s+/g, '') || value.trim();
  }
};

const getSelectedReportYear = (formValues?: FetchFormResponse): string => {
  const answers = formValues?.answers || {};
  const answerYear = parseReportPeriodYear(
    answers['report-period'] ?? answers['person-details-report-period'],
  );
  if (answerYear) return answerYear;

  return parseReportPeriodYear((formValues as { reportPeriod?: unknown } | undefined)?.reportPeriod);
};

const getReportingYearStart = (value: string): number | null => {
  const match = value.trim().match(/^(\d{4})[-/](\d{2}|\d{4})$/);
  return match?.[1] ? Number(match[1]) : null;
};

const getCurrentReportingYearStart = (): number => {
  const { year, month } = getLondonDateParts();
  return month >= 4 ? year : year - 1;
};

export const isSectionAvailable = (
  section: Section,
  formValues?: FetchFormResponse,
): boolean => {
  const selectedReportYearStart = getReportingYearStart(getSelectedReportYear(formValues));
  const currentReportingYearStart = getCurrentReportingYearStart();
  const sectionQuarter = getSectionQuarter(section);

  // Quarter sections stay locked until report year is explicitly selected.
  if (sectionQuarter && selectedReportYearStart === null) return false;

  if (!section.availableFrom) return true;

  if (
    selectedReportYearStart !== null &&
    selectedReportYearStart !== currentReportingYearStart
  ) {
    return true;
  }

  const { year, month, day } = getLondonDateParts();
  const availableMonth = Number(section.availableFrom.month);
  const availableDay = Number(section.availableFrom.day);
  const availableYearOffset = Number(section.availableFrom.yearOffset || 0);
  const availableYear =
    (selectedReportYearStart ?? currentReportingYearStart) + availableYearOffset;

  if (year > availableYear) return true;
  if (year < availableYear) return false;
  if (month > availableMonth) return true;
  if (month < availableMonth) return false;
  return day >= availableDay;
};

export type SubmitGuardViolation = {
  subsectionSlug: string;
  questionName: string;
  message: string;
};

export const hasBlockedSubmitGuardValue = (
  question: Question,
  answers: FetchFormResponse['answers'],
): boolean => {
  const submitGuard = question.submitGuard;
  if (!submitGuard || !Array.isArray(submitGuard.blockedValues)) return false;

  const rawAnswer = answers[question.name];
  const normalizedBlocked = new Set(
    submitGuard.blockedValues.map((value) => String(value).trim().toLowerCase()),
  );

  if (Array.isArray(rawAnswer)) {
    return rawAnswer.some((value) => normalizedBlocked.has(String(value).trim().toLowerCase()));
  }

  const normalizedAnswer = String(rawAnswer ?? '').trim().toLowerCase();
  if (!normalizedAnswer) return false;
  return normalizedBlocked.has(normalizedAnswer);
};

export const getEnabledSubsections = (
  formConfig: FormConfig,
  fetchFormResponse: FetchFormResponse = {
    answers: {},
  },
): Record<string, Subsection> => {
  // returns a Record<string, Subsection> map of subsections
  // Only really filters the pages, not the subsections.
  // Looks like it would include a subsection whose pages are all disabled.

  // Do a deep clone of the config so we can't corrupt the config
  const clonedConfig: FormConfig = JSON.parse(JSON.stringify(formConfig));
  const { answers, drafts } = fetchFormResponse;
  const draftPages: string[] = drafts ? Object.values(drafts).flat() : [];
  const draftQuestions: string[] = [];

  const filtered: Record<string, Subsection> = {};
  clonedConfig.sections?.forEach((section) => {
    section.subsections.forEach((subsection) => {
      if (
        formConfig.id === 'gdad' &&
        subsection.dynamicPages?.type === 'gdadSkillsByRoleLevel'
      ) {
        const { buildGdadSkillPages } = require('../config/pages/gdad/buildSkillPages') as typeof import('../config/pages/gdad/buildSkillPages');
        subsection.pages = buildGdadSkillPages(subsection, fetchFormResponse);
      }
    filtered[subsection.slug] = subsection;
    if (drafts) {
        subsection.pages.forEach((page) => {
          // Check both "page-name" and "page-name:list-item-id" formats
          const hasPageDraft = draftPages.some(
            (draft) => draft === page.name || draft.startsWith(`${page.name}:`),
          );

          if (hasPageDraft && 'questions' in page) {
            page.questions?.forEach((question) => draftQuestions.push(question.name));
          }
        });
      }
    });
  });

  Object.keys(filtered).forEach((key) => {
    filtered[key].pages = filtered[key].pages.filter(
      (page) =>
        !isPageDisabled(page, fetchFormResponse) &&
        !page.disabled?.find(
          ({ field }) => draftQuestions.includes(field) && !answers[field],
        ),
    );
  });

  return filtered;
};

export const isPageCompleted = (
  questions: Question[],
  answers: FetchFormResponse['answers'],
): boolean => {
  // Treat pages as incomplete when a submitGuard "blocked value" is selected anywhere on the page.
  // This keeps task list / summary statuses aligned with the submission guard behaviour.
  const extractedQuestions = extractConditionalQuestions(questions as any);
  if (extractedQuestions.some((question) => hasBlockedSubmitGuardValue(question, answers))) {
    return false;
  }
  return !!extractedQuestions?.find((q) => q.name in answers);
};

export const isListCompleted = (
  questions: Question[],
  answers: FetchFormResponse['answers'],
): boolean => {
  let completed = true;

  questions.forEach((question) => {
    const isDisplayOnlyQuestion =
      question.questionType === 'inset' || question.questionType === 'content';
    if (
      !isDisplayOnlyQuestion &&
      !question.name.endsWith('-inset') &&
      !Object.keys(answers).find((q) => q.endsWith(question.name))
    ) {
      completed = false;
    }
  });

  return completed;
};

// Find whether any variation of each question's name is present in the answers
export const isAnswered = (answers: FetchFormResponse['answers'], questions: Question[]) => {
  const answerKeys = Object.keys(answers);
  return questions.some(({ name, questionType }) => {
    if (questionType === 'content') return false;
    return answerKeys.some((key) => key.includes(name));
  });
};

export type ConditionalChildRouting = {
  hasChangeTypeButton: boolean;
  controllerField?: string;
  controllerPageName?: string;
  controllerStatus: Status;
  currentChildPageName?: string;
};

const getPageDraftStatus = (
  drafts: FetchFormResponse['drafts'] | undefined,
  subsectionSlug: string,
  pageName: string,
) =>
  Array.isArray(drafts?.[subsectionSlug]) &&
  drafts![subsectionSlug].some((draftId) => draftId.split(':')[0] === pageName);

const getQuestionsForPage = (page: Page): Question[] =>
  page.pageType === 'information' ? [] : extractConditionalQuestions(page.questions || []);

const getControllerFieldForPages = (pages: Page[]): string | undefined => {
  const pageWithChangeTypeButton = pages.find((page) => page.changeTypeButton === true);
  return pageWithChangeTypeButton?.disabled?.[0]?.field;
};

export const getConditionalChildRouting = (
  allPages: Page[],
  enabledPages: Page[],
  formValues: FetchFormResponse,
  subsectionSlug: string,
): ConditionalChildRouting => {
  const visibleAllPages = allPages.filter((page) => !isWorkflowOnlyPageName(page.name));
  const visibleEnabledPages = enabledPages.filter((page) => !isWorkflowOnlyPageName(page.name));
  const hasChangeTypeButton = visibleAllPages.some((page) => page.changeTypeButton === true);

  if (!hasChangeTypeButton) {
    return { hasChangeTypeButton: false, controllerStatus: Status.NotStarted };
  }

  const controllerField = getControllerFieldForPages(visibleAllPages);
  if (!controllerField) {
    return { hasChangeTypeButton: true, controllerStatus: Status.NotStarted };
  }

  const controllerPage = visibleAllPages.find((page) =>
    getQuestionsForPage(page).some((question) => question.name === controllerField),
  );

  const controllerPageName = controllerPage?.name;
  let controllerStatus = Status.NotStarted;

  if (controllerPage && controllerPage.pageType !== 'information') {
    const controllerQuestions = getQuestionsForPage(controllerPage);
    const hasControllerDraft = getPageDraftStatus(formValues.drafts, subsectionSlug, controllerPage.name);
    controllerStatus = hasControllerDraft
      ? Status.InProgress
      : isPageCompleted(controllerQuestions, formValues.answers || {})
        ? Status.Completed
        : Status.NotStarted;
  }

  const candidateChildPages = visibleAllPages
    .filter((page) => page.name !== controllerPageName)
    .filter((page) => page.disabled?.some((rule) => rule.field === controllerField));

  const currentChildPageName = visibleEnabledPages.find((page) =>
    candidateChildPages.some((candidate) => candidate.name === page.name),
  )?.name;

  return {
    hasChangeTypeButton,
    controllerField,
    controllerPageName,
    controllerStatus,
    currentChildPageName,
  };
};

export const getOverviewStatus = (
  sections: Section[],
  subsectionMap: Record<string, Subsection>,
  formValues: FetchFormResponse = { answers: {} },
): Record<Subsection['slug'], Status> => {
  const { answers, drafts } = formValues;
  if (!answers) return {};

  const status = sections?.reduce(
    (sections, current) => {
      const subsections = current.subsections.reduce(
        (totalSubsections, subsection) => {
          const { pages, slug } = subsection;
          if (!isSectionAvailable(current, formValues)) {
            totalSubsections[slug] = Status.NotDue;
            return totalSubsections;
          }

          if (isSubsectionNotRequired(subsection, formValues)) {
            totalSubsections[slug] = Status.Completed;
            return totalSubsections;
          }

          const subsectionPages = subsectionMap[slug]?.pages || pages;
          const visiblePages = subsectionPages.filter((page) => !isWorkflowOnlyPageName(page.name));
          // Approval-only subsections are hidden from normal form-side progress.
          if (visiblePages.length === 0) {
            totalSubsections[slug] = Status.Completed;
            return totalSubsections;
          }
          // Compile a list of all the questions for this subsection
          // Only count drafts that belong to *this* subsection's pages
          // Compile a list of all the questions for this subsection
          const questions = visiblePages?.reduce((acc, page) => {
            if (page.pageType !== 'information' && page.questions) {
              acc.push(...page.questions);
            }
            return acc;
          }, [] as Question[]);

          // Only count drafts that belong to *this* subsection's pages
          // Handle both "page-name" and "page-name:list-item-id" formats
          const pageIdsInSubsection = (visiblePages ?? []).map((p) => p.name);
          const hasDraftHere =
            Array.isArray(drafts?.[slug]) &&
            drafts![slug].some((draftId) => {
              // Extract page name from draft identifier (handle "page-name" or "page-name:123" formats)
              const pageName = draftId.split(':')[0];
              return pageIdsInSubsection.includes(pageName);
            });

          const isCompleted = isAnswered(answers, questions);

          // Keep original precedence: drafts win over completion
          const isNilReturnSubsection = visiblePages.some((p) => p.type === 'nilreturn');

          let subsectionStatus = hasDraftHere
            ? Status.InProgress
            : isCompleted
              ? Status.Completed
              : isNilReturnSubsection
                ? Status.NilReturn
                : Status.NotStarted;

          const conditionalRouting = getConditionalChildRouting(
            pages,
            visiblePages,
            formValues,
            slug,
          );

          if (
            conditionalRouting.hasChangeTypeButton &&
            conditionalRouting.controllerStatus !== Status.Completed
          ) {
            totalSubsections[slug] = conditionalRouting.controllerStatus;
            return totalSubsections;
          }

          const listPages = visiblePages.filter((page) => page.pageType === 'list');
          if (listPages.length > 0) {
            const answerKeys = Object.keys(answers);
            const hasListResponses = listPages.some((page) => {
              if (!('questions' in page)) return false;
              const questions = page.questions || [];
              return questions.some((question) =>
                answerKeys.some((key) => key.endsWith(question.name) && hasValue(answers[key])),
              );
            });

            if (!hasListResponses) {
              const hasNonListResponses = visiblePages.some((page) => {
                if (page.pageType === 'information' || page.pageType === 'list') return false;
                if (!('questions' in page)) return false;
                return (page.questions || []).some((question) => hasValue(answers[question.name]));
              });

              subsectionStatus =
                hasDraftHere || hasNonListResponses ? Status.InProgress : Status.NotStarted;
            }

            const hasListBelowMinimum = listPages.some((page) => {
              const minimum = page.minimumRequiredListItems;
              if (!minimum) return false;
              const grouped = getListAnswers(
                answers,
                page.pageType === 'list' ? page.questions || [] : [],
              );
              const responseCount = Object.values(grouped).filter((entry) =>
                entry.some((item) => hasValue(item.answer)),
              ).length;
              return responseCount < minimum;
            });

            if (hasListBelowMinimum) {
              subsectionStatus = Status.InProgress;
            }
          }

          // If this subsection includes a contacts-type question (points of contact),
          // require at least one "Point of contact for queries" before marking complete.
          if (slug === 'points-of-contact') {
            const hasContactsQuestion = questions.some((q) => q.name === 'contacts-type');
            if (hasContactsQuestion) {
              const hasQueriesContact = Object.entries(answers).some(([key, value]) => {
                if (!key.includes('contacts-type')) return false;
                if (typeof value === 'string') return value === 'Point of contact for queries';
                if (Array.isArray(value)) return value.includes('Point of contact for queries');
                return false;
              });
              if (!hasQueriesContact) {
                // Empty subsection should remain Not Started; partial data stays In Progress.
                subsectionStatus = hasDraftHere
                  ? Status.InProgress
                  : isCompleted
                    ? Status.InProgress
                    : Status.NotStarted;
              }
            }
          }

          if (questions.some((question) => hasBlockedSubmitGuardValue(question, answers))) {
            subsectionStatus = Status.InProgress;
          }

          totalSubsections[slug] = subsectionStatus;

          return totalSubsections;
        },
        {} as Record<string, Status>,
      );
      return { ...sections, ...subsections };
    },
    {} as Record<string, Status>,
  );

  return status;
};

export const getSubmitGuardViolations = (
  sections: Section[],
  subsectionMap: Record<string, Subsection>,
  formValues: FetchFormResponse = { answers: {} },
): SubmitGuardViolation[] => {
  const answers = formValues.answers || {};
  const violations: SubmitGuardViolation[] = [];

  sections.forEach((section) => {
    section.subsections.forEach((subsection) => {
      const subsectionPages = subsectionMap[subsection.slug]?.pages || subsection.pages;
      const visiblePages = subsectionPages.filter((page) => !isWorkflowOnlyPageName(page.name));
      const questions = visiblePages.reduce((acc, page) => {
        if (page.pageType !== 'information' && page.questions) {
          acc.push(...extractConditionalQuestions(page.questions));
        }
        return acc;
      }, [] as Question[]);

      questions.forEach((question) => {
        if (hasBlockedSubmitGuardValue(question, answers)) {
          const message = question.submitGuard?.message?.trim();
          if (message) {
            violations.push({
              subsectionSlug: subsection.slug,
              questionName: String(question.name),
              message,
            });
          }
        }
      });
    });
  });

  return violations;
};

export const getSectionCompletionStatus = (
  sections: Section[],
  subsectionMap: Record<string, Subsection>,
  formValues: FetchFormResponse = { answers: {} },
): Record<string, boolean> => {
  const subsectionStatuses = getOverviewStatus(sections, subsectionMap, formValues);

  return sections.reduce(
    (result, section) => {
      const isSectionComplete = section.subsections.every((subsection) =>
        COMPLETED_SUBSECTION_STATUSES.has(
          subsectionStatuses[subsection.slug] ?? Status.NotStarted,
        ),
      );
      result[section.slug] = isSectionComplete;
      return result;
    },
    {} as Record<string, boolean>,
  );
};

export const getLockedSections = (
  sections: Section[],
  subsectionMap: Record<string, Subsection>,
  formValues: FetchFormResponse = { answers: {} },
): Record<string, boolean> => {
  const completionBySection = getSectionCompletionStatus(sections, subsectionMap, formValues);

  return sections.reduce(
    (result, section) => {
      if (!section.dependsOn) {
        result[section.slug] = !isSectionAvailable(section, formValues);
        return result;
      }

      const dependsOnExists = Object.prototype.hasOwnProperty.call(
        completionBySection,
        section.dependsOn,
      );

      result[section.slug] =
        !isSectionAvailable(section, formValues) ||
        (dependsOnExists ? !completionBySection[section.dependsOn] : false);
      return result;
    },
    {} as Record<string, boolean>,
  );
};

export const getLockedSubsections = (
  sections: Section[],
  subsectionMap: Record<string, Subsection>,
  formValues: FetchFormResponse = { answers: {} },
): Record<string, boolean> => {
  const completionBySubsection = getOverviewStatus(sections, subsectionMap, formValues);

  return sections.reduce(
    (result, section) => {
      section.subsections.forEach((subsection) => {
        if (!subsection.dependsOn) {
          result[subsection.slug] = false;
          return;
        }

        const dependencySlugs = Array.isArray(subsection.dependsOn)
          ? subsection.dependsOn
          : [subsection.dependsOn];

        result[subsection.slug] = dependencySlugs.some((dependencySlug) => {
          const dependencyExists = Object.prototype.hasOwnProperty.call(
            completionBySubsection,
            dependencySlug,
          );
          return (
            dependencyExists &&
            !COMPLETED_SUBSECTION_STATUSES.has(completionBySubsection[dependencySlug])
          );
        });
      });

      return result;
    },
    {} as Record<string, boolean>,
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const extractConditionalQuestions = (questions: any[]) => {
  let extractedQuestions: Question[] = [];
  questions.forEach((question) => {
    extractedQuestions.push(question);

    // extract conditional questions from options
    if (Array.isArray(question.options)) {
      question.options.forEach((option: { questions: Question[] }) => {
        if (isObject(option) && 'questions' in option && option?.questions) {
          extractedQuestions = extractedQuestions.concat(
            extractConditionalQuestions(option.questions),
          );
        }
      });
    }
  });
  return extractedQuestions;
};

export const getConditionalQuestionNames = (questions: Question[]): string[] => [
  ...new Set(
    extractConditionalQuestions(questions)
      .map((question) => question.name)
      .filter((name): name is string => typeof name === 'string' && name.length > 0),
  ),
];

export const getConditionalQuestionDefaultValues = (
  questions: Question[],
): Record<string, string | string[]> =>
  extractConditionalQuestions(questions).reduce(
    (defaults, question) => {
      if (typeof question.name !== 'string' || question.name.length === 0) return defaults;

      defaults[question.name] = Array.isArray(question.defaultValue)
        ? question.defaultValue
        : question.defaultValue ?? '';
      return defaults;
    },
    {} as Record<string, string | string[]>,
  );

export const getSelectedConditionalQuestions = (question: Question, answer: string | string[]) => {
  const conditionalQuestions: Question[] = [];
  if (Array.isArray(question.options)) {
    // get conditional questions which have been selected
    question.options.forEach((option) => {
      if (isObject(option) && 'questions' in option && option.questions) {
        const optionValue = getSelectOptionValue(option);
        if (optionValue === answer || answer?.includes(optionValue)) {
          conditionalQuestions.push(...option.questions);
        }
      }
    });
  }
  return conditionalQuestions;
};

export const renderAnswer = (
  question: Question,
  value: unknown,
): string | string[] | Record<string, string> => {
  const isScientificNotation = (rawValue: string) => /^-?\d+(?:\.\d+)?e[+-]?\d+$/i.test(rawValue);

  const formatValueWithDisplaySeperator = (rawValue: string | number): string => {
    if (!question.displaySeperator) return String(rawValue);

    const normalized = String(rawValue).replace(/,/g, '').trim();
    if (isScientificNotation(normalized)) {
      const parsed = Number(normalized);
      if (Number.isFinite(parsed)) {
        return parsed.toLocaleString('en-GB', { maximumFractionDigits: 20 });
      }
    }
    if (!/^-?\d+(?:\.\d+)?$/.test(normalized)) return String(rawValue);

    const isNegative = normalized.startsWith('-');
    const unsignedValue = isNegative ? normalized.slice(1) : normalized;
    const [whole = '', fraction] = unsignedValue.split('.', 2);
    const formattedWhole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const formattedNumber = fraction !== undefined ? `${formattedWhole}.${fraction}` : formattedWhole;

    return isNegative ? `-${formattedNumber}` : formattedNumber;
  };

  // exit if value is empty
  if (!value) return '';

  if (question.questionType === 'reportPeriod' && isJsonString(value)) {
    try {
      const parsed = JSON.parse(String(value));
      const quarter = typeof parsed?.quarter === 'string' ? parsed.quarter : '';
      const year = typeof parsed?.year === 'string' ? parsed.year : '';
      return [quarter, year].filter(Boolean).join(' ').trim();
    } catch (_err) {
      // fall through to default rendering
    }
  }
  if (question.questionType === 'reportMonth' && isJsonString(value)) {
    try {
      const parsed = JSON.parse(String(value));
      const month = typeof parsed?.month === 'string' ? parsed.month : '';
      const year = typeof parsed?.year === 'string' ? parsed.year : '';
      return [month, year].filter(Boolean).join(' ').trim();
    } catch (_err) {
      // fall through to default rendering
    }
  }
  if (question.questionType === 'developmentProgramme' && isJsonString(value)) {
    try {
      const parsed = JSON.parse(String(value));
      const programme = typeof parsed?.programme === 'string' ? parsed.programme : '';
      const cohortYear = typeof parsed?.cohortYear === 'string' ? parsed.cohortYear : '';
      const otherProgramme =
        typeof parsed?.otherProgramme === 'string' ? parsed.otherProgramme : '';
      const displayProgramme = programme === 'Other' && otherProgramme ? otherProgramme : programme;
      return [displayProgramme, cohortYear].filter(Boolean).join(' - ').trim();
    } catch (_err) {
      // fall through to default rendering
    }
  }

  if (question.displaySeperator && (typeof value === 'string' || typeof value === 'number')) {
    return formatValueWithDisplaySeperator(value);
  }

  if (
    (question.questionType === 'select' ||
      question.questionType === 'radio' ||
      question.questionType === 'checkbox') &&
    (typeof value === 'string' || typeof value === 'number')
  ) {
    return getDisplayLabelForStoredSelectValue(question.options, value) ?? String(value);
  }

  // if value is a json string, parse it
  if (isJsonString(value)) {
    return JSON.parse(String(value));
  }

  // plain string values
  if (typeof value === 'string') {
    // date fields
    if (question.questionType === 'date' || question.questionType === 'shortDate') {
      let returnVal = '';
      if (dateIsFullyPopulated(value)) {
        const dateValues = value?.split('-');

        try {
          const monthDesc = Intl.DateTimeFormat('en', { month: 'long' }).format(
            new Date(dateValues[1] ?? ''),
          );
          returnVal = `${dateValues[2] ?? ''} ${monthDesc} ${dateValues[0] ?? ''}`;
        } catch (err) {
          // fallback on invalid date format
          return '';
        }
      }

      return returnVal.trim();
    }

    if (question.displaySeperator) {
      return formatValueWithDisplaySeperator(value);
    }

    // all other fields e.g. basic textfields
    return value;
  }

  if (typeof value === 'number') {
    if (question.displaySeperator) {
      return value.toLocaleString('en-GB');
    }
    return String(value);
  }

  if (Array.isArray(value)) {
    return value
      .filter((answer) => typeof answer === 'string' && !!answer)
      .map((answer) =>
        question.questionType === 'checkbox'
          ? getDisplayLabelForStoredSelectValue(question.options, answer) ?? answer
          : answer,
      );
  }

  if (!!value && typeof value === 'object') {
    // Address formatting
    if ('postcode' in value && 'line1' in value) {
      const addressOrder = ['line1', 'line2', 'town', 'county', 'postcode'];

      return addressOrder
        .map((field) => String(value[field as keyof typeof value] ?? ''))
        .filter((answer) => !!answer);
    }

    return Object.keys(value)
      .map((key) => {
        const result = value[key as keyof typeof value];
        return result ? String(result).trim() : '';
      })
      .filter((answer) => !!answer);
  }

  // fallback
  return '';
};

export const isJsonString = (value: unknown) => {
  try {
    JSON.parse(String(value));
    return true;
  } catch (error) {
    return false;
  }
};

export const isJsonObject = (value: unknown) => {
  return typeof value === 'object' && !Array.isArray(value) && value !== null;
};
